import { useParams, useNavigate } from "react-router-dom";
import { Building2, Mail, Phone, MapPin, Users, MonitorSmartphone, Calendar, Globe } from "lucide-react";
import { buildTenantActions } from "@/components/actions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTenants, useTenantInvitations, useTenantDeletions } from "@/hooks/useTenants";
import { TENANT_STATUS_CONFIG, INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS } from "@/types/tenant";
import { format } from "date-fns";
import { useState } from "react";
import { RequestDeletionDialog } from "@/components/tenants/RequestDeletionDialog";
import { DetailPageLayout } from "@/components/ui/detail-page-layout";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function TenantDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getTenantById } = useTenants();
  const { data: tenant, isLoading } = getTenantById(id || "");
  const { invitations } = useTenantInvitations(id);
  const { deletions } = useTenantDeletions();
  const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

  const pendingDeletion = deletions.find(d => d.customer_id === id && d.status === 'pending');

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Tenant Not Found</h2>
          <p className="text-muted-foreground mb-4">The tenant you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/admin/tenants")}>Back to Tenants</Button>
        </div>
      </div>
    );
  }

  const statusConfig = TENANT_STATUS_CONFIG[tenant.tenant_status] || TENANT_STATUS_CONFIG.active;
  const industryLabel = INDUSTRY_OPTIONS.find(i => i.value === tenant.industry)?.label || tenant.industry;
  const companySizeLabel = COMPANY_SIZE_OPTIONS.find(s => s.value === tenant.company_size)?.label || tenant.company_size;

  const actions = buildTenantActions({
    surface: "detail",
    canRequestDeletion: tenant.tenant_status !== "deleted" && !pendingDeletion,
    onView: () => {},
    onEdit: () => navigate(`/admin/tenants/${id}/edit`),
    onRequestDeletion: () => setDeletionDialogOpen(true),
  });

  return (
    <DetailPageLayout
      backLabel="Back to Tenants"
      onBack={() => navigate("/admin/tenants")}
      
      actions={actions}
      icon={
        tenant.logo_url ? (
          <img src={tenant.logo_url} alt={tenant.name} className="w-16 h-16 rounded-xl object-cover" />
        ) : (
          <Building2 className="h-8 w-8 text-primary" />
        )
      }
      title={tenant.name}
      subtitle={tenant.email}
      badges={
        <>
          <Badge variant="secondary" className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
            {statusConfig.label}
          </Badge>
          {pendingDeletion && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Deletion Pending
            </Badge>
          )}
        </>
      }
      dialogs={
        <RequestDeletionDialog
          open={deletionDialogOpen}
          onOpenChange={setDeletionDialogOpen}
          tenantId={id}
          tenantName={tenant.name}
        />
      }
    >
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Devices Enrolled</CardTitle>
            <MonitorSmartphone className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{tenant.device_count}</div>
            {tenant.expected_device_count && tenant.expected_device_count > 0 && (
              <p className="text-xs text-muted-foreground">Expected: {tenant.expected_device_count}</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admin Invitations</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{invitations.length}</div>
            <p className="text-xs text-muted-foreground">
              {invitations.filter(i => i.status === 'pending').length} pending
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Created</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {tenant.created_at ? format(new Date(tenant.created_at), "MMM d") : "-"}
            </div>
            <p className="text-xs text-muted-foreground">
              {tenant.created_at ? format(new Date(tenant.created_at), "yyyy") : ""}
              {tenant.onboarded_by && ` · by ${tenant.onboarded_by}`}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Business Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Business Information</CardTitle>
              <CardDescription>Company details and profile</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Industry</label>
                  <p className="font-medium">{industryLabel || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Company Size</label>
                  <p className="font-medium">{companySizeLabel || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Subscription Tier</label>
                  <p className="font-medium capitalize">{tenant.subscription_tier || "-"}</p>
                </div>
                {tenant.partner_id && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Partner ID</label>
                    <p className="font-medium font-mono text-sm">{tenant.partner_id}</p>
                  </div>
                )}
              </div>
              {tenant.domains && tenant.domains.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Domains</label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {tenant.domains.map((domain) => (
                      <Badge key={domain} variant="outline" className="font-mono text-xs">
                        <Globe className="h-3 w-3 mr-1" />
                        {domain}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
              <CardDescription>Primary contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>{tenant.email}</span>
              </div>
              {tenant.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{tenant.phone}</span>
                </div>
              )}
              {(tenant.address || tenant.city || tenant.state) && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    {tenant.address && <p>{tenant.address}</p>}
                    <p>{[tenant.city, tenant.state, tenant.zip].filter(Boolean).join(", ")}</p>
                    {tenant.country && <p>{tenant.country}</p>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Billing Contact */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Billing Contact</CardTitle>
              <CardDescription>Billing and invoice details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Contact Name</label>
                <p className="font-medium">{tenant.billing_contact_name || "-"}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Contact Email</label>
                <p className="font-medium">{tenant.billing_contact_email || "-"}</p>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          {(tenant.updated_at || tenant.updated_by) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Metadata</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {tenant.updated_at && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
                    <p className="text-sm">{format(new Date(tenant.updated_at), "MMM d, yyyy h:mm a")}</p>
                  </div>
                )}
                {tenant.updated_by && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                    <p className="text-sm">{tenant.updated_by}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Admin Invitations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Admin Invitations</CardTitle>
              <CardDescription>Invited administrators</CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <p className="text-muted-foreground text-sm">No admin invitations sent</p>
              ) : (
                <div className="space-y-3">
                  {invitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div>
                        <p className="font-medium text-sm">{invitation.name || invitation.email}</p>
                        <p className="text-xs text-muted-foreground">{invitation.email}</p>
                      </div>
                      <Badge variant={
                        invitation.status === 'accepted' ? 'default' :
                        invitation.status === 'pending' ? 'secondary' :
                        'outline'
                      }>
                        {invitation.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DetailPageLayout>
  );
}
